import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User } from '@/types/auth';
import { AuthService } from '@/services/authService';
import { CreditoService } from '@/services/creditoService';
import { useSocket } from '@/hooks/useSocket';

// Tipo para notificações do socket
interface UserNotification {
  type: string;
  novoSaldo?: number;
  [key: string]: unknown;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (updatedUser: Partial<User>) => void;
  refreshBalance: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const socket = useSocket({
    autoConnect: !!user,
    userId: user?.id,
    token: AuthService.getToken() ?? undefined,
  });

  const isAuthenticated = !!user && AuthService.isAuthenticated();
  
  const updateUser = useCallback((updatedUser: Partial<User>) => {
    setUser(prevUser => {
      if (!prevUser) return null;

      // Evitar atualizações desnecessárias: compare valores realmente alterados
      const keys = Object.keys(updatedUser) as (keyof User)[];
      const hasChange = keys.some(key => updatedUser[key] !== undefined && updatedUser[key] !== prevUser[key]);

      if (!hasChange) {
        return prevUser; // nada mudou
      }

      const newUser = { ...prevUser, ...updatedUser };
      // Evitar regravar cookie a cada refresh; só grava se não estivermos em ambiente dev ou se dados críticos mudarem
      // AuthService.setCurrentUser(newUser);
      return newUser;
    });
  }, []);

  useEffect(() => {
    if (user && socket.socket && socket.isConnected) {
      
      
      const handleSaldoUpdate = (data: { novoSaldo: number }) => {
        
        updateUser({ saldo: data.novoSaldo });
      };

      const handleUserNotification = (data: UserNotification) => {
        
        if (data.type === 'saldo_atualizado' && data.novoSaldo !== undefined) {
          
          updateUser({ saldo: data.novoSaldo });
        }
      };

      // Escutar eventos de saldo
      socket.socket.on('saldo_atualizado', handleSaldoUpdate);
      socket.socket.on('user_notification', handleUserNotification);

      return () => {
        if (socket.socket) {
          
          socket.socket.off('saldo_atualizado', handleSaldoUpdate);
          socket.socket.off('user_notification', handleUserNotification);
        }
      };
    } else if (user && !socket.isConnected) {
      // usuário desconectado: nada a fazer
    }
  }, [user, socket.socket, socket.isConnected, updateUser]);

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const token = AuthService.getToken();
        if (token) {
          // Token existe, então buscamos os dados mais recentes do usuário
          const freshUser = await AuthService.getMe();
          
          // Fallback: Se o saldo não vier do backend, buscar explicitamente
          if (freshUser.saldo === undefined || freshUser.saldo === null) {
            try {
              const saldo = await CreditoService.getSaldoUsuario(freshUser.id);
              freshUser.saldo = saldo;
            } catch (saldoError) {
              freshUser.saldo = 0; // Saldo padrão em caso de erro
            }
          }
          
          setUser(freshUser);
        }
      } catch (error) {
        await logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await AuthService.login({ email, password });
      if (response.success && response.user) {
        setUser(response.user);
      } else {
        throw new Error(response.message || 'Erro ao fazer login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await AuthService.logout();
    } catch {
      // ignorar erros ao deslogar
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      if (AuthService.isAuthenticated()) {
        
        const userData = await AuthService.getMe();
        
        setUser(userData);
        // Atualiza o localStorage
      }
    } catch (error) {
      
      await logout();
    }
  };

  const refreshBalance = async () => {
    if (!user?.id) {
      
      return;
    }

    try {
      const novoSaldo = await CreditoService.getSaldoUsuario(user.id);
      updateUser({ saldo: novoSaldo });
    } catch {
      // ignorar erros ao atualizar saldo
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
    updateUser,
    refreshBalance,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}; 